import { useEffect, useState } from "react";
import api from "../lib/api";

const CURRS = ["USD", "EUR", "GBP", "INR"];
const SYM = { USD: "$", EUR: "€", GBP: "£", INR: "₹" };

/**
 * Compact 12-month weekend-revenue sparkline for the given FY.
 * Renders one polyline per currency (in the currency's brand shade),
 * with a tiny legend + hover tooltip on each dot.
 */
export default function WeekendRevenueChart({ fy }) {
  const [data, setData] = useState(null);
  const [hover, setHover] = useState(null); // {month, values}

  useEffect(() => {
    api.get(`/sales/weekend-monthly?fy=${fy}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ months: [] }));
  }, [fy]);

  if (!data) return null;
  const months = data.months || [];
  if (months.length === 0) return null;

  // Compute max across all currencies to normalise the y-axis.
  const maxVal = Math.max(1, ...months.flatMap(m => CURRS.map(c => m[c] || 0)));
  const anySpend = months.some(m => CURRS.some(c => (m[c] || 0) > 0));

  const W = 320, H = 90, padL = 24, padR = 8, padT = 8, padB = 18;
  const innerW = W - padL - padR, innerH = H - padT - padB;
  const step = months.length > 1 ? innerW / (months.length - 1) : innerW;
  const yFor = (v) => padT + innerH - (v / maxVal) * innerH;
  const xFor = (i) => padL + i * step;

  const colors = { USD: "#1B7A3E", EUR: "#0EA5E9", GBP: "#F97316", INR: "#8B5CF6" };
  // Skip currencies that had zero spend all 12 months.
  const activeCurrs = CURRS.filter(c => months.some(m => (m[c] || 0) > 0));

  const monthLabel = (m) => {
    const [yr, mm] = m.split("-");
    const d = new Date(`${yr}-${mm}-01`);
    return d.toLocaleString("default", { month: "short" });
  };

  return (
    <div className="aii-card p-4" data-testid="weekend-chart">
      <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--aii-muted)]">Weekend revenue · monthly trend</div>
          <div className="text-sm font-display font-semibold">FY {data.fy}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {activeCurrs.length === 0
            ? <span className="text-[11px] italic text-[color:var(--aii-muted)]">No weekend sales this FY yet</span>
            : activeCurrs.map(c => (
                <span key={c} className="text-[10px] flex items-center gap-1 font-semibold" data-testid={`wchart-legend-${c}`}>
                  <span className="w-2.5 h-2.5 rounded-sm" style={{ background: colors[c] }}/>
                  {c}
                </span>
              ))
          }
        </div>
      </div>
      {anySpend ? (
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-24" data-testid="wchart-svg">
          {/* Baseline */}
          <line x1={padL} y1={padT + innerH} x2={W - padR} y2={padT + innerH} stroke="#E5EBE9" strokeWidth="1"/>
          {/* Month ticks */}
          {months.map((m, i) => (
            <text key={`t-${m}`} x={xFor(i)} y={H - 4} fontSize="8" fill="#798782" textAnchor="middle">
              {monthLabel(m.month)}
            </text>
          ))}
          {/* Y max label */}
          <text x={padL - 2} y={padT + 4} fontSize="8" fill="#798782" textAnchor="end">
            {maxVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </text>
          {/* Lines per currency */}
          {activeCurrs.map(c => {
            const pts = months.map((m, i) => `${xFor(i)},${yFor(m[c] || 0)}`).join(" ");
            return (
              <polyline
                key={`ln-${c}`}
                fill="none" stroke={colors[c]} strokeWidth="1.6"
                points={pts}
                data-testid={`wchart-line-${c}`}
              />
            );
          })}
          {/* Dots */}
          {months.map((m, i) => (
            activeCurrs.map(c => (m[c] || 0) > 0 && (
              <circle key={`d-${c}-${m.month}`} cx={xFor(i)} cy={yFor(m[c])} r="2.5"
                      fill={colors[c]}
                      onMouseEnter={() => setHover({ month: m.month, values: m })}
                      onMouseLeave={() => setHover(null)}
                      style={{ cursor: "pointer" }}
              />
            ))
          ))}
        </svg>
      ) : (
        <div className="text-xs text-[color:var(--aii-muted)] italic py-6 text-center">
          Nothing to plot yet — approved weekend bookings will appear here month-by-month.
        </div>
      )}
      {hover && (
        <div className="text-[11px] text-[color:var(--aii-text-2)] mt-1 flex items-center gap-2 flex-wrap" data-testid="wchart-hover">
          <b>{hover.month}</b>
          {activeCurrs.map(c => (
            <span key={`h-${c}`}>{SYM[c]}{(hover.values[c] || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} <span className="text-[color:var(--aii-muted)]">{c}</span></span>
          ))}
        </div>
      )}
    </div>
  );
}
